import {
  Component,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { RANGO_MAX, RANGO_MIN } from '../../data/filters.data';
import { formatRangeLabel } from '../../models/date-range.model';
import { FilterField } from '../../models/filter-option.model';
import { ViewportService } from '../../services/viewport.service';

/** Todo lo que puede recibir foco dentro del panel lateral. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Referencia estable para los campos sin selección: evita recrear el arreglo
 *  en cada ciclo de detección y disparar ngOnChanges del multi-select. */
const SIN_VALORES: Array<string> = [];

/** Pastillas que se dejan ver en la barra pegajosa de un teléfono chico. */
const CHIPS_VISIBLES_XS = 2;

/** Contador de instancias: da un id único al título del panel. */
let drawerSequence = 0;

/** Clave del campo de rango dentro de la selección. */
const CAMPO_RANGO = 'rango';

/** Campos de periodo que dicen lo mismo que el rango, en versión rápida. */
const CAMPOS_PERIODO = ['anio', 'mes'] as const;

/** Un valor activo de un campo, listo para pintarse como pastilla. */
export interface IFilterChip {
  /** Clave del campo (`mes`, `sucursal`, …). */
  key: string;
  /** Etiqueta del campo («Mes»). */
  label: string;
  /** Valor crudo, el que se quita de la selección. */
  value: string;
  /** Etiqueta legible del valor («Julio»). */
  text: string;
}

/**
 * Barra pegajosa de filtros. Sustituye al panel de 11 campos que se repetía en
 * cada pantalla: en la barra sólo viven los filtros ACTIVOS, como pastillas que
 * se quitan de una en una, y la edición completa se hace en un panel lateral.
 *
 * La piel es `.z-filter-bar`, `.z-chip-filter` y `.z-drawer*` (styles.scss);
 * aquí está el comportamiento: borrador, Escape, click en el fondo, foco
 * atrapado y scroll del body bloqueado mientras el panel está abierto.
 */
@Component({
  selector: 'app-filter-bar',
  standalone: false,
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnChanges, OnDestroy {

  @Input() public fields: Array<FilterField> = [];
  @Input() public selected: Record<string, Array<string>> = {};
  @Output() public selectedChange = new EventEmitter<Record<string, Array<string>>>();

  public open = false;
  public chips: Array<IFilterChip> = [];

  /** Selección en edición dentro del panel; no sale hasta pulsar «Aplicar». */
  public draft: Record<string, Array<string>> = {};

  public readonly titleId = `z-filter-drawer-title-${++drawerSequence}`;

  public readonly rangoMin = RANGO_MIN;
  public readonly rangoMax = RANGO_MAX;

  @ViewChild('drawer') private drawerRef?: ElementRef<HTMLElement>;
  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLButtonElement>;

  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly viewport = inject(ViewportService);

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR CICLO DE VIDA                            */
  /* -------------------------------------------------------------------------- */

  public ngOnChanges(): void {
    this.buildChips();
  }

  public ngOnDestroy(): void {
    if (this.open) {
      this.releaseScroll();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR PASTILLAS                              */
  /* -------------------------------------------------------------------------- */

  public get activeCount(): number {
    return this.chips.length;
  }

  /**
   * En `xs` la barra sólo enseña las dos primeras pastillas; el resto se resume
   * en un «+N» que abre el panel.
   *
   * Seis pastillas de dos líneas cada una dejaban la barra pegajosa ocupando
   * media pantalla, y como está pegada bajo la barra superior, esa mitad se
   * pierde en todo el recorrido. Se recortan a dos en vez de colapsarlas a un
   * «6 filtros activos» porque el conteo ya lo lleva la insignia del botón
   * Filtros: un resumen sería el mismo dato dos veces, y la barra dejaría de
   * responder a la única pregunta que justifica que esté pegada — qué estoy
   * viendo. Con dos pastillas se conserva el quitar de un toque.
   */
  public get visibleChips(): Array<IFilterChip> {
    return this.viewport.breakpoint() === 'xs'
      ? this.chips.slice(0, CHIPS_VISIBLES_XS)
      : this.chips;
  }

  public get hiddenChipCount(): number {
    return this.chips.length - this.visibleChips.length;
  }

  /** Quitar una pastilla se aplica en el acto: es un gesto explícito. */
  public removeChip(chip: IFilterChip): void {
    const next = this.normalize(this.selected);
    const values = (next[chip.key] ?? []).filter((value) => value !== chip.value);

    if (values.length) {
      next[chip.key] = values;
    } else {
      delete next[chip.key];
    }

    this.commit(next);
  }

  private buildChips(): void {
    const chips: Array<IFilterChip> = [];

    for (const field of this.fields) {
      for (const value of this.selected?.[field.key] ?? []) {
        chips.push({
          key: field.key,
          label: field.label,
          value,
          text: this.textoValor(field, value),
        });
      }
    }

    this.chips = chips;
  }

  /** El rango no tiene catálogo de opciones: su etiqueta se compone. */
  private textoValor(field: FilterField, value: string): string {
    if (field.type === 'range') {
      return formatRangeLabel(value) || value;
    }

    return field.options.find((option) => option.value === value)?.label ?? value;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR PANEL                                 */
  /* -------------------------------------------------------------------------- */

  public openDrawer(): void {
    if (this.open) {
      return;
    }

    this.draft = this.normalize(this.selected);
    this.open = true;
    this.lockScroll();
    // El panel se pinta en el siguiente ciclo: el foco va después.
    setTimeout(() => this.drawerRef?.nativeElement.focus());
  }

  public closeDrawer(): void {
    if (!this.open) {
      return;
    }

    this.open = false;
    this.releaseScroll();
    this.triggerRef?.nativeElement.focus();
  }

  public draftFor(key: string): Array<string> {
    return this.draft[key] ?? SIN_VALORES;
  }

  /**
   * Año, mes y rango son el mismo eje —el periodo— dicho de dos maneras, y
   * combinarlos produce contradicciones sin lectura posible («2026 · Julio ·
   * del 3 de marzo al 9 de abril»). Elegir uno retira el otro, y el pie del
   * campo lo avisa antes de que pase.
   */
  public onDraftChange(key: string, values: Array<string>): void {
    const siguiente = { ...this.draft, [key]: values };

    if (values.length && CAMPOS_PERIODO.includes(key as (typeof CAMPOS_PERIODO)[number])) {
      delete siguiente[CAMPO_RANGO];
    }

    this.draft = siguiente;
  }

  public draftRango(): string {
    return (this.draft[CAMPO_RANGO] ?? [])[0] ?? '';
  }

  public onRangoChange(value: string): void {
    const siguiente = { ...this.draft };

    if (value) {
      siguiente[CAMPO_RANGO] = [value];

      for (const campo of CAMPOS_PERIODO) {
        delete siguiente[campo];
      }
    } else {
      delete siguiente[CAMPO_RANGO];
    }

    this.draft = siguiente;
  }

  /** Aviso bajo el campo cuando lo que se elija va a retirar lo ya puesto. */
  public avisoPeriodo(field: FilterField): string {
    if (field.type === 'range') {
      return this.tienePeriodo() ? 'Elegir un rango retira Año y Mes.' : '';
    }

    if (!CAMPOS_PERIODO.includes(field.key as (typeof CAMPOS_PERIODO)[number])) {
      return '';
    }

    return this.draftRango() ? 'Elegir Año o Mes retira el rango de fechas.' : '';
  }

  private tienePeriodo(): boolean {
    return CAMPOS_PERIODO.some((campo) => (this.draft[campo] ?? []).length > 0);
  }

  /** Vacía el borrador; el cambio no sale hasta «Aplicar». */
  public clearAll(): void {
    this.draft = {};
  }

  public apply(): void {
    this.commit(this.normalize(this.draft));
    this.closeDrawer();
  }

  private commit(value: Record<string, Array<string>>): void {
    this.selected = value;
    this.buildChips();
    this.selectedChange.emit(this.selected);
  }

  /** Copia la selección quitando los campos sin valores. */
  private normalize(source: Record<string, Array<string>>): Record<string, Array<string>> {
    const result: Record<string, Array<string>> = {};

    for (const key of Object.keys(source ?? {})) {
      const values = source[key];

      if (values?.length) {
        result[key] = [...values];
      }
    }

    return result;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR TECLADO                               */
  /* -------------------------------------------------------------------------- */

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (!this.open) {
      return;
    }

    if (event.key === 'Escape') {
      // Un desplegable abierto dentro del panel ya consumió la tecla: cerrarlo
      // no debe cerrar además el panel entero.
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  /** Mantiene el tabulador dentro del panel mientras está abierto. */
  private trapFocus(event: KeyboardEvent): void {
    const root = this.drawerRef?.nativeElement;

    if (!root) {
      return;
    }

    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((element) => element.offsetParent !== null);
    const active = this.document.activeElement as HTMLElement | null;

    if (!items.length) {
      event.preventDefault();
      root.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    if (!active || !root.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR SCROLL DEL BODY                            */
  /* -------------------------------------------------------------------------- */

  /** Bloquea el scroll del fondo y compensa el ancho de la barra para que la
   *  página no salte al abrir. */
  private lockScroll(): void {
    const body = this.document.body;
    const gap = window.innerWidth - this.document.documentElement.clientWidth;

    // `overflow-y` y no el atajo `overflow`: el atajo pisaría el
    // `overflow-x: clip` del body (§8 del contrato) por `hidden`, que sí crea
    // contenedor de desplazamiento y deja sin efecto todos los `position:
    // sticky` de la página — empezando por esta misma barra.
    this.renderer.setStyle(body, 'overflow-y', 'hidden');

    if (gap > 0) {
      this.renderer.setStyle(body, 'padding-right', `${gap}px`);
    }
  }

  private releaseScroll(): void {
    this.renderer.removeStyle(this.document.body, 'overflow-y');
    this.renderer.removeStyle(this.document.body, 'padding-right');
  }
}
